import { useEffect, useState } from 'react'
import { apiFetch } from '../api/client'
import { createNadDebug } from '../utils/debugLog'

// Cache key + TTL — landing-assets URLs are signed for 24h on the server;
// we refresh ahead of that so a long-lived tab doesn't flip to broken
// signatures mid-session.
//
// Bump the version suffix whenever the signed slug SET changes (add/remove a
// gallery asset). A returning same-tab visitor holds a cached map for up to
// 22h and skips the refetch; without a new key they would keep rendering the
// stale set (e.g. a map missing `corgi-garden` → empty card) until TTL/tab
// close. The `_v3` bump forces a one-time resign after splitting original
// desktop URLs from optimized mobile URLs.
const CACHE_KEY = 'dreamwright_landing_assets_signed_v3'
const CACHE_TTL_MS = 22 * 60 * 60 * 1000
const landingAssetLog = createNadDebug('dw:mobile-ui-loading')

interface SignedLandingAssets {
  urls: Record<string, string>
  optimizedUrls: Record<string, string>
}

const EMPTY_SIGNED_ASSETS: SignedLandingAssets = { urls: {}, optimizedUrls: {} }

let inFlight: Promise<SignedLandingAssets> | null = null

// `JSON.parse` and `Response.json()` both return `any` from lib.dom typings.
// Project lint forbids the `as` cast keyword AND the `unknown` keyword, so
// we narrow with runtime `typeof` checks against the implicit-`any` returns
// rather than via type-guard helpers (which would need an `unknown` param).
function validateUrlMap(raw: { [k: string]: string } | null): Record<string, string> | null {
  if (!raw || typeof raw !== 'object') return null
  for (const v of Object.values(raw)) {
    if (typeof v !== 'string') return null
  }
  return raw
}

function readCache(): SignedLandingAssets | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const fetchedAt = parsed.fetchedAt
    if (typeof fetchedAt !== 'number') return null
    if (Date.now() - fetchedAt > CACHE_TTL_MS) return null
    const urls = validateUrlMap(parsed.urls ?? null)
    const optimizedUrls = validateUrlMap(parsed.optimizedUrls ?? {})
    if (urls === null || optimizedUrls === null) return null
    return { urls, optimizedUrls }
  } catch {
    return null
  }
}

function writeCache(assets: SignedLandingAssets): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        fetchedAt: Date.now(),
        urls: assets.urls,
        optimizedUrls: assets.optimizedUrls,
      }),
    )
  } catch {
    // sessionStorage can throw under quota / privacy modes — non-fatal.
  }
}

function fetchSignedLandingAssets(): Promise<SignedLandingAssets> {
  if (inFlight) {
    landingAssetLog({
      event: 'landing-assets-fetch-joined',
      feature: 'mobile-ui-loading',
    })
    return inFlight
  }

  landingAssetLog({
    event: 'landing-assets-fetch-start',
    feature: 'mobile-ui-loading',
  })

  inFlight = apiFetch('/landing-assets/sign')
    .then(async (res) => {
      if (!res.ok) throw new Error(`landing-assets sign failed: ${res.status}`)
      const body = await res.json()
      const urls = validateUrlMap(body?.urls ?? null)
      const optimizedUrls = validateUrlMap(body?.optimized_urls ?? {})
      if (urls === null || optimizedUrls === null) {
        throw new Error('landing-assets sign returned an invalid body')
      }
      const assets = { urls, optimizedUrls }
      writeCache(assets)
      landingAssetLog({
        event: 'landing-assets-fetch-success',
        feature: 'mobile-ui-loading',
        asset_count: Object.keys(urls).length,
        optimized_asset_count: Object.keys(optimizedUrls).length,
      })
      return assets
    })
    .catch((err) => {
      landingAssetLog({
        event: 'landing-assets-fetch-failed',
        feature: 'mobile-ui-loading',
        error: err instanceof Error ? err.message : String(err),
      })
      throw err
    })
    .finally(() => {
      inFlight = null
    })

  return inFlight
}

export interface UseSignedLandingAssetsOptions {
  enabled?: boolean
}

export interface UseSignedLandingAssetsResult {
  /** Slug → signed original URL. Empty object until the first fetch resolves. */
  urls: Record<string, string>
  /** Slug → signed optimized URL. Consumers choose whether to fall back to urls. */
  optimizedUrls: Record<string, string>
  /** True while the initial fetch is in flight. */
  loading: boolean
  /** Set if the fetch failed; consumers fall back to poster/static UX. */
  error: Error | null
}

/**
 * Fetch V4-signed GCS URLs for the landing-v2 video gallery.
 *
 * The landing-assets bucket is private — clients must call
 * `GET /landing-assets/sign` to obtain time-bounded URLs before referencing
 * them in `<video src>` / `<img src>`. The endpoint is anonymous; no auth
 * header is required.
 *
 * Results are cached in `sessionStorage` for ~22h so navigating between
 * landing-page sections (or refreshing the tab) does not re-sign on every
 * mount. The cache is per-tab; a fresh tab pays the round-trip once.
 */
export function useSignedLandingAssets(
  options: UseSignedLandingAssetsOptions = {},
): UseSignedLandingAssetsResult {
  const enabled = options.enabled ?? true
  const [assets, setAssets] = useState<SignedLandingAssets>(() => readCache() ?? EMPTY_SIGNED_ASSETS)
  const [loading, setLoading] = useState<boolean>(() => enabled && readCache() === null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const cached = readCache()
    if (cached !== null) {
      // sessionStorage hit — state is already correct; skip the fetch.
      setAssets(cached)
      setLoading(false)
      setError(null)
      return
    }

    if (!enabled) {
      landingAssetLog({
        event: 'landing-assets-fetch-disabled',
        feature: 'mobile-ui-loading',
      })
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchSignedLandingAssets()
      .then((validated) => {
        if (cancelled) return
        setAssets(validated)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  return { urls: assets.urls, optimizedUrls: assets.optimizedUrls, loading, error }
}
